// Query/fragment params that carry credentials: the emailed reset/invite `token_hash`, tokens
// a provider may put in a redirect fragment, and the OAuth authorization `code`. They must never
// reach PostHog or Sentry through a captured URL, breadcrumb or transaction name.
export const SENSITIVE_URL_PARAMS = ['token_hash', 'access_token', 'refresh_token', 'code'] as const;

export const FILTERED_VALUE = '[Filtered]';

// Fields filtered by name wherever they appear (e.g. Sentry's parsed `query_string` object).
// `code` is left out: as a field name it is far more often an error code than an OAuth code.
const SENSITIVE_FIELD_NAMES: readonly string[] = ['token_hash', 'access_token', 'refresh_token'];

const SENSITIVE_PARAM_PATTERN = new RegExp(`(^|[?&#;])(${SENSITIVE_URL_PARAMS.join('|')})=[^&#\\s"'<>]*`, 'gi');

// Deep enough for Sentry events and PostHog properties; deeper payloads are left as they are.
const MAX_DEPTH = 12;

/** Replaces the value of every sensitive param found in a URL, query string or free text. */
export const scrubSensitiveParams = (value: string): string =>
  value.includes('=') ? value.replace(SENSITIVE_PARAM_PATTERN, `$1$2=${FILTERED_VALUE}`) : value;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const scrubValue = (value: unknown, depth: number): unknown => {
  if (typeof value === 'string') {
    return scrubSensitiveParams(value);
  }
  if (depth >= MAX_DEPTH) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        SENSITIVE_FIELD_NAMES.includes(key) && typeof item === 'string' ? FILTERED_VALUE : scrubValue(item, depth + 1),
      ]),
    );
  }
  // Dates, class instances and primitives other than strings carry no URL.
  return value;
};

/**
 * Returns a copy of an analytics payload (PostHog capture result, Sentry event or breadcrumb)
 * with every sensitive param scrubbed from its strings and every token field filtered.
 */
export const scrubSensitiveData = <T>(payload: T): T => scrubValue(payload, 0) as T;
